import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { adminApi, type Conductor, type ConductorSortField, type ConductorType } from '../api/admin';
import { DataListFrame, SortHeader, stickyTh, useListControls } from '../components/data-list';

// 種別の表示ラベル（日本語）。DB保存値はコード（task_board / chat_reporter）。
const typeOptions: { value: ConductorType; label: string; hint: string }[] = [
  { value: 'task_board', label: 'タスク掲示板', hint: '仕事（タスク）を取ってくる相手' },
  { value: 'chat_reporter', label: 'チャット報告者', hint: '報告・相談する相手' },
];
const typeLabel = (t: ConductorType) => typeOptions.find((o) => o.value === t)?.label ?? t;

const SORT_OPTIONS: { value: ConductorSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: '名前' },
  { value: 'type', label: '種別' },
  { value: 'createdAt', label: '作成日' },
];

// コンダクター（連携先）= システムがやりとりする相手の住所録。
// 種別：①タスク掲示板（仕事を取ってくる相手）②チャット報告者（報告・相談する相手）。
// 登録対象の例：外部MCP、チャット相手（上村さん本人）など。今回は登録画面のみ（取得/報告の連携動作は別タスク）。
export function ConductorsPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['conductors'] });

  const controls = useListControls<ConductorSortField>('konecco.list.conductors', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // マスタ的な項目（種別/状態）= ドロップダウン絞り込み。'' は指定なし。
  const [typeFilter, setTypeFilter] = useState<'' | ConductorType>('');
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');
  const conductorsQuery = useQuery({
    queryKey: [
      'conductors',
      'search',
      controls.q,
      typeFilter,
      enabledFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchConductors({
        q: controls.q,
        type: typeFilter || undefined,
        enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const items = conductorsQuery.data?.items ?? [];
  const total = conductorsQuery.data?.total ?? 0;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Conductor | null>(null);

  const createConductor = useMutation({
    mutationFn: adminApi.createConductor,
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });
  const updateConductor = useMutation({
    mutationFn: (vars: {
      id: number;
      input: {
        name?: string;
        type?: ConductorType;
        endpoint?: string | null;
        note?: string | null;
        enabled?: boolean;
      };
    }) => adminApi.updateConductor(vars.id, vars.input),
    onSuccess: () => {
      setEditTarget(null);
      invalidate();
    },
  });
  const deleteConductor = useMutation({
    mutationFn: adminApi.deleteConductor,
    onSuccess: invalidate,
  });
  // 一覧から直接「有効/無効」を切り替える（履歴のため削除せず無効化できる）。
  const toggleEnabled = (c: Conductor) =>
    updateConductor.mutate({ id: c.id, input: { enabled: !c.enabled } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        システムがやりとりする相手（連携先）の住所録。仕事を取ってくる「タスク掲示板」や、報告・相談する「チャット報告者」を登録します。
        ここは登録だけで、実際の取得・報告の動作は別途実装します。
      </p>
      {conductorsQuery.error && (
        <p className="k-error">取得に失敗しました: {(conductorsQuery.error as Error).message}</p>
      )}
      {deleteConductor.error && (
        <p className="k-error">削除に失敗しました: {(deleteConductor.error as Error).message}</p>
      )}
      <DataListFrame
        title="コンダクター"
        total={total}
        loading={conductorsQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（名前・メモ・宛先）"
        filters={
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                種別
              </span>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as '' | ConductorType);
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                {typeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                状態
              </span>
              <select
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value as '' | 'true' | 'false');
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                <option value="true">有効</option>
                <option value="false">無効</option>
              </select>
            </label>
          </>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortHeader field="id" controls={controls}>id</SortHeader>
              <SortHeader field="name" controls={controls}>名前</SortHeader>
              <SortHeader field="type" controls={controls}>種別</SortHeader>
              <th style={stickyTh}>宛先</th>
              <th style={stickyTh}>状態</th>
              <th style={stickyTh}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{c.id}</td>
                <td style={td}>
                  <strong>{c.name}</strong>
                  {c.note && (
                    <div className="k-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {c.note}
                    </div>
                  )}
                </td>
                <td style={td}>
                  <span style={chip}>{typeLabel(c.type)}</span>
                </td>
                <td style={{ ...td, maxWidth: 260, color: '#475569', wordBreak: 'break-all' }}>
                  {c.endpoint ? c.endpoint : <span className="k-muted">—</span>}
                </td>
                <td style={td}>
                  <button
                    className="btn-ghost"
                    type="button"
                    disabled={updateConductor.isPending}
                    title="クリックで有効/無効を切替"
                    onClick={() => toggleEnabled(c)}
                    style={{ color: c.enabled ? '#16a34a' : '#94a3b8', fontWeight: 700 }}
                  >
                    {c.enabled ? '● 有効' : '○ 無効'}
                  </button>
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" type="button" onClick={() => setEditTarget(c)}>
                      変更
                    </button>
                    <button
                      className="btn-danger"
                      type="button"
                      disabled={deleteConductor.isPending}
                      onClick={() => {
                        if (confirm(`連携先「${c.name}」を削除しますか？`)) {
                          deleteConductor.mutate(c.id);
                        }
                      }}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataListFrame>

      {createOpen && (
        <ConductorFormDialog
          mode="create"
          submitting={createConductor.isPending}
          error={createConductor.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createConductor.mutate(v)}
        />
      )}
      {editTarget && (
        <ConductorFormDialog
          mode="edit"
          initial={editTarget}
          submitting={updateConductor.isPending}
          error={updateConductor.error as Error | null}
          onClose={() => setEditTarget(null)}
          onSubmit={(v) => updateConductor.mutate({ id: editTarget.id, input: v })}
        />
      )}
    </div>
  );
}

interface ConductorFormValues {
  name: string;
  type: ConductorType;
  endpoint: string | null;
  note: string | null;
  enabled: boolean;
}

function ConductorFormDialog({
  mode,
  initial,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: Conductor;
  onSubmit: (v: ConductorFormValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<ConductorType>(initial?.type ?? 'task_board');
  const [endpoint, setEndpoint] = useState(initial?.endpoint ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;
  const selectedHint = typeOptions.find((o) => o.value === type)?.hint;

  return (
    <Modal
      title={mode === 'create' ? '新しい連携先を登録' : `連携先を変更 — #${initial?.id}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSubmit({
            name: name.trim(),
            type,
            endpoint: endpoint.trim() || null,
            note: note.trim() || null,
            enabled,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={fieldWrap}>
          <label style={labelStyle}>名前（例: 営業タスク掲示板 / 上村さん）</label>
          <input
            placeholder="例: 営業タスク掲示板"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>種別</label>
          <select value={type} onChange={(e) => setType(e.target.value as ConductorType)}>
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {selectedHint && (
            <span className="k-muted" style={{ fontSize: 12 }}>
              {selectedHint}
            </span>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>宛先（任意・URL / MCP名 / Slackチャンネル等）</label>
          <input
            placeholder="例: https://… / sales-stocars / #report"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>メモ（任意）</label>
          <textarea
            placeholder="連携先の補足説明"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          有効にする（無効にしても履歴のため残ります）
        </label>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim()}>
            {mode === 'create' ? '登録' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 460,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
const chip = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: '#eef2ff',
  color: '#4338ca',
} as const;
